/**
 * Shared peek-cart drawer UI: backdrop dim, item rows, footer total.
 */
(() => {
  const BACKDROP_ID = 'kl24-cart-drawer-backdrop';

  const getBackdrop = () => {
    let el = document.getElementById(BACKDROP_ID);
    if (!el) {
      el = document.createElement('div');
      el.id = BACKDROP_ID;
      el.className = 'kl24-cart-drawer-backdrop';
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
      document.body.append(el);
    }
    return el;
  };

  /** @type {(() => void) | null} */
  let activeClose = null;

  const ensureFooterTotal = (drawer) => {
    const footer = drawer?.querySelector('.tcg-gap-cart-drawer__footer');
    if (!footer) return null;
    let total = footer.querySelector('[data-kl24-cart-drawer-total]');
    if (!total) {
      total = document.createElement('div');
      total.className = 'tcg-gap-cart-drawer__total';
      total.dataset.kl24CartDrawerTotal = '';
      total.innerHTML = `
        <span class="tcg-gap-cart-drawer__total-label">Zwischensumme</span>
        <span class="tcg-gap-cart-drawer__total-value" data-kl24-cart-drawer-total-value></span>`;
      const hint = footer.querySelector('[data-kl24-cart-checkout-hint]');
      if (hint) footer.insertBefore(total, hint);
      else footer.prepend(total);
    }
    return total;
  };

  const updateFooterTotal = (drawer, { qty, formattedTotal }) => {
    const total = ensureFooterTotal(drawer);
    if (!total) return;
    const valueEl = total.querySelector('[data-kl24-cart-drawer-total-value]');
    const labelEl = total.querySelector('.tcg-gap-cart-drawer__total-label');
    if (!qty) {
      total.hidden = true;
      return;
    }
    total.hidden = false;
    if (labelEl) {
      labelEl.textContent = qty === 1 ? '1 Karte' : `${qty} Karten`;
    }
    if (valueEl) valueEl.textContent = `ca. ${formattedTotal}`;
  };

  const createThumb = (imageUrl, alt) => {
    const wrap = document.createElement('div');
    wrap.className = 'tcg-gap-cart-drawer__item-thumb';
    if (imageUrl) {
      const api = window.Kl24ShopifyImage;
      const sized =
        api?.sizedUrl(imageUrl, api.WIDTHS?.cartThumb ?? 120) || imageUrl;
      const img = document.createElement('img');
      img.className = 'tcg-gap-cart-drawer__item-thumb-img';
      img.src = sized;
      img.alt = alt || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 50;
      img.height = 70;
      wrap.append(img);
    } else {
      const ph = document.createElement('span');
      ph.className = 'tcg-gap-cart-drawer__item-thumb-ph';
      ph.setAttribute('aria-hidden', 'true');
      wrap.append(ph);
    }
    return wrap;
  };

  const finishCssModifier = (label) =>
    window.Kl24CartFinish?.finishCssModifier(label) || 'matt';

  const buildItemRow = ({ name, detail, finishLabel, priceFormatted, qty, imageUrl, onRemove }) => {
    const li = document.createElement('li');
    li.className = 'tcg-gap-cart-drawer__item';

    li.append(createThumb(imageUrl, name));

    const body = document.createElement('div');
    body.className = 'tcg-gap-cart-drawer__item-body';

    const info = document.createElement('div');
    info.className = 'tcg-gap-cart-drawer__item-info';
    const nameEl = document.createElement('p');
    nameEl.className = 'tcg-gap-cart-drawer__item-name';
    nameEl.textContent = name;

    const metaEl = document.createElement('div');
    metaEl.className = 'tcg-gap-cart-drawer__item-meta';
    if (detail) {
      const detailEl = document.createElement('p');
      detailEl.className = 'tcg-gap-cart-drawer__item-meta-line tcg-gap-cart-drawer__item-set';
      detailEl.textContent = detail;
      metaEl.append(detailEl);
    }
    if (finishLabel) {
      const finishRow = document.createElement('p');
      finishRow.className = 'tcg-gap-cart-drawer__item-meta-line tcg-gap-cart-drawer__item-finish-row';
      const badge = document.createElement('span');
      badge.className = `kl24-cart-item-finish kl24-cart-item-finish--${finishCssModifier(finishLabel)}`;
      badge.textContent = finishLabel;
      finishRow.append(badge);
      metaEl.append(finishRow);
    }

    info.append(nameEl, metaEl);

    const row = document.createElement('div');
    row.className = 'tcg-gap-cart-drawer__item-row';

    const priceEl = document.createElement('span');
    priceEl.className = 'tcg-gap-cart-drawer__item-price';
    priceEl.textContent = priceFormatted;

    const qtyEl = document.createElement('span');
    qtyEl.className = 'tcg-gap-cart-drawer__item-qty';
    qtyEl.textContent = `×${qty}`;
    qtyEl.setAttribute('aria-label', `Stückzahl: ${qty}`);

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'tcg-gap-cart-drawer__item-remove';
    removeBtn.setAttribute('aria-label', `${name} entfernen`);
    removeBtn.textContent = 'Entfernen';
    removeBtn.addEventListener('click', onRemove);

    row.append(priceEl, qtyEl, removeBtn);
    body.append(info, row);
    li.append(body);
    return li;
  };

  const cartItemImage = (item) => {
    if (!item) return '';
    if (typeof item.image === 'string' && item.image) return item.image;
    if (item.featured_image?.url) return item.featured_image.url;
    return '';
  };

  window.Kl24CartDrawerUI = {
    open(drawer, onBackdropClick) {
      if (!drawer) return;
      drawer.classList.add('is-open');
      const backdrop = getBackdrop();
      backdrop.hidden = false;
      activeClose = onBackdropClick;
      backdrop.onclick = () => activeClose?.();
    },
    close(drawer) {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      const backdrop = getBackdrop();
      backdrop.hidden = true;
      backdrop.onclick = null;
      activeClose = null;
    },
    ensureFooterTotal,
    updateFooterTotal,
    buildItemRow,
    cartItemImage,
  };
})();
